// app/components/ServiceLinksWithMedia.client.tsx
"use client";

import * as React from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import clsx from "clsx";
import { LazyMotion, domAnimation, m, type MotionProps, useReducedMotion } from "framer-motion";
import type { MuxCSSProperties } from "@mux/mux-player-react";
import Link from "next/link";

const MuxPlayer = dynamic(() => import("@mux/mux-player-react"), {
    ssr: false,
    loading: () => <div className="h-full w-full bg-secondary/20" />,
});

type LinkItem = {
    id: string;
    label: string;
    href: string;
    index: number;
};

type Media = { playbackId: string; posterTime?: number };

type BaseProps = {
    heading: string;
    items: LinkItem[];
    media?: Media;
};

type ConflictingHtmlEvents =
    | "onAnimationStart"
    | "onAnimationEnd"
    | "onAnimationIteration"
    | "onDrag"
    | "onDragStart"
    | "onDragEnd"
    | "onDragEnter"
    | "onDragLeave"
    | "onDragOver"
    | "onDrop";

type SafeSectionHtmlProps = Omit<
    React.ComponentPropsWithoutRef<"section">,
    keyof BaseProps | keyof MotionProps | ConflictingHtmlEvents
>;

export type ServiceLinksWithMediaClientProps = BaseProps & SafeSectionHtmlProps & MotionProps;

const slideUp = {
    hidden: { y: 40, opacity: 0 },
    visible: (i: number) => ({
        y: 0,
        opacity: 1,
        transition: { duration: 0.8, ease: [0.215, 0.61, 0.355, 1], delay: i * 0.1 },
    }),
} as const;

function shouldAvoidAutoplay(): boolean {
    if (typeof navigator === "undefined") return false;
    const anyNav = navigator as any;
    const conn = anyNav.connection || anyNav.mozConnection || anyNav.webkitConnection;
    const saveData = Boolean(conn?.saveData);
    const effectiveType: string | undefined = conn?.effectiveType;
    const slow = effectiveType === "slow-2g" || effectiveType === "2g";
    return saveData || slow;
}

function useNear(ref: React.RefObject<HTMLElement | null>, rootMargin = "600px 0px"): boolean {
    const [near, setNear] = React.useState(false);

    React.useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) {
                    setNear(true);
                    io.disconnect();
                }
            },
            { rootMargin, threshold: 0 }
        );

        io.observe(el);
        return () => io.disconnect();
    }, [ref, rootMargin]);

    return near;
}

export default function ServiceLinksWithMediaClient({
                                                        heading,
                                                        items,
                                                        media,
                                                        className,
                                                        ...rest
                                                    }: ServiceLinksWithMediaClientProps) {
    const sectionRef = React.useRef<HTMLElement | null>(null);

    const reduced = useReducedMotion() ?? false;
    const avoidAutoplay = React.useMemo(() => shouldAvoidAutoplay(), []);
    const canAutoplay = !reduced && !avoidAutoplay;

    const shouldLoadPlayer = useNear(sectionRef, "600px 0px");
    const [isVideoReady, setIsVideoReady] = React.useState(false);

    const posterUrl = React.useMemo(() => {
        if (!media?.playbackId) return undefined;
        const t = media.posterTime ?? 1;
        return `https://image.mux.com/${media.playbackId}/thumbnail.jpg?time=${encodeURIComponent(t)}`;
    }, [media?.playbackId, media?.posterTime]);

    const playerStyle: MuxCSSProperties = React.useMemo(
        () => ({
            "--controls": "none",
            "--media-object-fit": "cover",
            "--media-object-position": "center",
            opacity: isVideoReady ? 1 : 0,
            transition: "opacity 0.8s ease-in-out",
        }),
        [isVideoReady]
    );

    return (
        <LazyMotion features={domAnimation} strict>
            <m.section
                id="relume"
                ref={sectionRef}
                initial="hidden"
                animate="visible"
                className={clsx("px-[5%] rounded-3xl bg-secondary py-16 md:py-24 lg:py-28", className)}
                {...rest}
            >
                <div className="container text-primary">
                    {/* ✅ TABLET: mniejszy nagłówek żeby “WYJAZDY” się mieściło
              ✅ DESKTOP: ogromny jak był */}
                    <m.h2
                        variants={slideUp}
                        custom={0}
                        className={clsx(
                            "mb-8 whitespace-nowrap uppercase font-semibold leading-none",
                            "text-[clamp(2.35rem,8vw,7.4rem)] md:mb-10",
                            "lg:mb-12 lg:text-[11rem]"
                        )}
                    >
                        {heading}
                    </m.h2>

                    {/* ✅ TABLET: układ jak mobile (1 kolumna)
              ✅ DESKTOP (lg+): wraca 2 kolumny */}
                    <div className="grid items-start gap-8 lg:grid-cols-2 lg:gap-x-12 lg:gap-16">
                        <div>
                            {/* na tablecie nie dawaj dużych offsetów */}
                            <div className="mt-6 md:mt-8 lg:mt-18">
                                <ul className="divide-y divide-white/15">
                                    {items.map((it, idx) => {
                                        const no = String(it.index + 1).padStart(2, "0");
                                        return (
                                            <m.li key={it.id} variants={slideUp} custom={idx + 1} className="py-7 md:py-8">
                                                <Link
                                                    href={it.href}
                                                    scroll={true}
                                                    className={clsx(
                                                        "group grid grid-cols-[auto,1fr,auto] items-center gap-4",
                                                        "[-webkit-tap-highlight-color:transparent]"
                                                    )}
                                                >
                          <span
                              className={clsx(
                                  "w-10 tabular-nums",
                                  "text-sm md:text-base lg:text-md",
                                  "[@media(hover:hover)]:transition-colors [@media(hover:hover)]:duration-300",
                                  "[@media(hover:hover)]:group-hover:text-accent"
                              )}
                          >
                            {no}
                          </span>

                                                    {/* ✅ TABLET: zmniejszamy etykiety żeby się mieściły w wierszu */}
                                                    <span
                                                        className={clsx(
                                                            "text-base md:text-lg lg:text-2xl",
                                                            "[@media(hover:hover)]:transition-colors [@media(hover:hover)]:duration-300",
                                                            "[@media(hover:hover)]:group-hover:text-accent"
                                                        )}
                                                    >
                            {it.label}
                          </span>

                                                    <span
                                                        aria-hidden
                                                        className={clsx(
                                                            "grid size-8 place-items-center rounded-full bg-primary",
                                                            "[@media(hover:hover)]:transition-all [@media(hover:hover)]:duration-300",
                                                            "[@media(hover:hover)]:group-hover:bg-accent",
                                                            "[@media(hover:hover)]:group-hover:-translate-y-0.5",
                                                            "[@media(hover:hover)]:group-hover:translate-x-0.5"
                                                        )}
                                                    >
                            <img src="/arrow.svg" alt="" className="size-5" />
                          </span>
                                                </Link>
                                            </m.li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>

                        {/* ✅ TABLET: video jest pod listą (brak sticky)
                ✅ DESKTOP: sticky jak było */}
                        <m.div
                            variants={slideUp}
                            custom={2}
                            className="relative aspect-video w-full overflow-hidden ios-clip-fix rounded-3xl bg-black/10 lg:sticky lg:top-32"
                        >
                            {media?.playbackId && posterUrl ? (
                                <>
                                    <Image
                                        src={posterUrl}
                                        alt=""
                                        fill
                                        priority
                                        sizes="(min-width: 1024px) 50vw, 100vw"
                                        className="object-cover"
                                    />

                                    {shouldLoadPlayer && canAutoplay && (
                                        <MuxPlayer
                                            playbackId={media.playbackId}
                                            poster={posterUrl}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                            streamType="on-demand"
                                            nohotkeys
                                            onLoadedData={() => setIsVideoReady(true)}
                                            className="absolute inset-0 z-10 h-full w-full object-cover pointer-events-none"
                                            style={playerStyle}
                                        />
                                    )}

                                    <div className="absolute inset-0 z-20 bg-black/10 pointer-events-none" />
                                </>
                            ) : (
                                <div className="h-full w-full bg-black/30" />
                            )}
                        </m.div>
                    </div>
                </div>
            </m.section>
        </LazyMotion>
    );
}